import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';
import Button, { ButtonStyle } from '../../common/components/Button/Button';
import ButtonContainer from '../../common/components/Button/ButtonContainer';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import UserInfo from '../../common/components/UserInfo/UserInfo';
import SVGError from '../../common/assets/svg/icons/SVGError';
import { ServiceErrorType, UserErrorResponse, UserResponseOptions } from '../../services/common/serviceError';

const customStyle = {
  content: {
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    top: '10px',
    minWidth: '400px',
    marginRight: '-50%',
    transform: 'translate(-50%, 0px)',
    background: 'rgb(var(--default-background))',
    borderRadius: '25px',
  },
};

export interface ServiceErrorModalProps {
  showModal: boolean;
  errorType: ServiceErrorType | null;
  options: UserResponseOptions;
  onUserErrorResponse: (response: UserErrorResponse) => void;
}

export default function ServiceErrorModal({
  showModal,
  options,
  onUserErrorResponse,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  errorType,
}: ServiceErrorModalProps): JSX.Element {
  const { t } = useTranslation();

  const onClose = () => {
    onUserErrorResponse('fail');
  };

  const onRetry = () => {
    onUserErrorResponse('retry');
  };

  return (
    <ReactModal isOpen={showModal} style={customStyle}>
      <div className="content-container flex flex-col justify-center items-center flex-grow-0 flex-shrink-0 mt-2">
        <header className="flex-grow-0 flex-shrink-0 flex justify-center items-center mb-4">
          <SVGError />
        </header>

        <TitleContainer center>
          <h1 className="mb-4 text-textHeadline" data-testid="ERROR_TITLE">
            {t('screens.errorModal.title')}
          </h1>
        </TitleContainer>

        <ButtonContainer>
          <>
            {(options & UserResponseOptions.retry) === UserResponseOptions.retry && (
              <Button style={ButtonStyle.DEFAULT} dataTestid="RETRY_BUTTON" onClick={onRetry} disabled={false}>
                <span>{t('screens.errorModal.retryButton')}</span>
              </Button>
            )}
          </>
          <>{options === UserResponseOptions.all && <div className="pb-3" />}</>
          <>
            {(options & UserResponseOptions.fail) === UserResponseOptions.fail && (
              <Button style={ButtonStyle.INVERTED} dataTestid="CLOSE_BUTTON" onClick={onClose} disabled={false}>
                <span>{t('screens.errorModal.closeButton')}</span>
              </Button>
            )}
          </>
        </ButtonContainer>
        <UserInfo />
      </div>
    </ReactModal>
  );
}
